// ════════════════════════════════════════════════════════════════════════════
// M1.5 Madurez de Estructura — dashboard (prompt 0728 F4)
// Heatmap 5×5 (dimensión × nivel) + radar VALOR·IA 5D + KPIs. Consume los
// endpoints /api/v1/structure/madurez/*. Estilo espejo de los dashboards de
// analytics (DM Sans, paleta AZUL, recharts, i18n con fallbacks).
// ════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback, useRef } from 'react';
import { useI18n } from '../../../../../i18n';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import {
  fetchMadurezEvaluaciones,
  crearMadurezEvaluacion,
  sembrarMadurezDimensiones,
  sembrarMadurezCuestionario,
  fetchMadurezHeatmap,
  aprobarMadurezEvaluacion,
  clonarMadurezEvaluacion,
  actualizarObservacionesMadurez,
  type MadurezEvaluacion,
  type MadurezHeatmap,
  type MadurezDimension,
  type NivelMadurez,
  type DimensionMadurez,
} from '../../../../shared/services/api';
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import { MadurezTimeline } from '../components/MadurezTimeline';
import { CuestionarioForm } from '../components/CuestionarioForm';
import {
  NIVELES_5D,
  NIVEL_INFO,
  PREGUNTA_DIMENSION,
  MARCO_TEORICO,
  descriptorCelda,
} from '../descriptores-madurez';
import { MadurezAnalisisIA } from '../components/MadurezAnalisisIA';
import { EscenariosWhatIf } from '../components/EscenariosWhatIf';

const C = {
  a1: '#1F3864',
  a2: '#2F5496',
  ce: '#33BCE1',
  go: '#595959',
  gm: '#D9D9D9',
  gc: '#F2F2F2',
  w: '#FFF',
};
const VERDE = '#16A34A';
const AMBAR = '#E0A800';
const ROJO = '#DC2626';

const DIM_ORDER: DimensionMadurez[] = [
  'FORMALIZACION',
  'GOBERNABILIDAD',
  'ALINEACION_ESTRATEGICA',
  'CAPACIDAD_CONTROL',
  'CAPACIDAD_EVOLUCION',
];
const NIVELES: NivelMadurez[] = ['M_1', 'M_2', 'M_3', 'M_4', 'M_5'];
const NIVEL_NUM: Record<NivelMadurez, number> = { M_0: 0, M_1: 1, M_2: 2, M_3: 3, M_4: 4, M_5: 5 };

function hexNivel(n: NivelMadurez): string {
  const v = NIVEL_NUM[n];
  return v >= 4 ? VERDE : v >= 2 ? AMBAR : ROJO;
}

const cardStyle: React.CSSProperties = {
  background: C.w,
  border: `0.5px solid ${C.gm}`,
  borderTop: `2px solid ${C.a1}`,
  borderRadius: 8,
  padding: '12px 14px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};

const SecTitle = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 800,
      color: C.a1,
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      paddingBottom: 4,
      borderBottom: `1.5px solid ${C.a1}`,
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

interface MadurezDashboardProps {
  embedded?: boolean;
}

export function MadurezDashboard({ embedded }: MadurezDashboardProps) {
  const { t } = useI18n();
  const mk = ((t.structure as Record<string, unknown>)?.madurez ?? {}) as Record<string, unknown>;
  const s = (k: string, fb: string): string => (typeof mk[k] === 'string' ? (mk[k] as string) : fb);
  const dimLabels = (mk.dimensiones ?? {}) as Record<string, string>;
  const nivLabels = (mk.niveles ?? {}) as Record<string, string>;
  const dimLabel = (d: DimensionMadurez): string => dimLabels[d] ?? d.replace(/_/g, ' ');
  const nivLabel = (n: NivelMadurez): string => nivLabels[n] ?? n.replace('_', '-');

  const [evaluaciones, setEvaluaciones] = useState<MadurezEvaluacion[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [heatmap, setHeatmap] = useState<MadurezHeatmap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [vista, setVista] = useState<'real' | 'cuestionario'>('real');
  // Conclusión del ingeniero de gobernanza (captura cualitativa, antes en MadurezAnalisisIA).
  const [observaciones, setObservaciones] = useState('');
  const [mostrarConclusion, setMostrarConclusion] = useState(true);
  const [savingObs, setSavingObs] = useState(false);
  const [obsGuardado, setObsGuardado] = useState(false);
  // Última evaluación seleccionada (para descartar refrescos de heatmap obsoletos).
  const selectedIdRef = useRef<string | null>(null);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const cargarLista = useCallback(async (preferId?: string) => {
    const res = await fetchMadurezEvaluaciones();
    setEvaluaciones(res.data);
    const next = preferId ?? res.data[0]?.id ?? null;
    setSelectedId(next);
    return next;
  }, []);

  useEffect(() => {
    cargarLista()
      .then(() => setLoading(false))
      .catch((err: Error) => {
        setError(err.message);
        setLoading(false);
      });
  }, [cargarLista]);

  useEffect(() => {
    if (!selectedId) return;
    fetchMadurezHeatmap(selectedId)
      .then((res) => setHeatmap(res.data))
      .catch((err: Error) => setError(err.message));
  }, [selectedId]);

  const crear = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await crearMadurezEvaluacion();
      await sembrarMadurezDimensiones(data.id);
      await cargarLista(data.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [cargarLista]);

  const aprobar = useCallback(async () => {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    try {
      await aprobarMadurezEvaluacion(selectedId);
      const res = await fetchMadurezHeatmap(selectedId);
      setHeatmap(res.data);
      await cargarLista(selectedId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [selectedId, cargarLista]);

  const clonar = useCallback(async () => {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await clonarMadurezEvaluacion(selectedId);
      await cargarLista(data.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [selectedId, cargarLista]);

  // Sincroniza la conclusión guardada al cambiar de evaluación o tras refrescar la lista.
  const obsActual = evaluaciones.find((e) => e.id === selectedId)?.observaciones ?? '';
  useEffect(() => {
    setObservaciones(obsActual);
    setObsGuardado(false);
  }, [selectedId, obsActual]);

  const guardarObs = useCallback(async () => {
    if (!selectedId) return;
    setSavingObs(true);
    setError(null);
    setObsGuardado(false);
    try {
      await actualizarObservacionesMadurez(selectedId, observaciones);
      setObsGuardado(true);
      await cargarLista(selectedId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingObs(false);
    }
  }, [selectedId, observaciones, cargarLista]);

  if (loading) {
    return (
      <div
        style={{
          height: embedded ? '100%' : 'calc(100vh - 124px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--wl-background, #ECEEF2)',
          fontFamily: "'DM Sans',sans-serif",
        }}
      >
        <LoadingSpinner text={s('loading', 'Cargando madurez de estructura…')} size="lg" />
      </div>
    );
  }

  const dimMap = new Map<DimensionMadurez, MadurezDimension>(
    (heatmap?.dimensiones ?? []).map((d) => [d.dimension, d]),
  );
  // Lectura activa por celda: REAL (organigrama) o CUESTIONARIO (percepción).
  const nivelDe = (dim: MadurezDimension | undefined): NivelMadurez | null =>
    !dim ? null : vista === 'real' ? dim.nivel : dim.nivelCuestionario;
  const radarData = DIM_ORDER.map((d) => {
    const n = nivelDe(dimMap.get(d));
    return { dimension: dimLabel(d), nivel: n ? NIVEL_NUM[n] : 0 };
  });
  // Nivel real vs. percepción del cuestionario por dimensión: alimenta la sección
  // de discrepancias del Análisis IA (reemplaza el panel de varianza retirado).
  const dimensionesAnalisis = DIM_ORDER.map((d) => {
    const dim = dimMap.get(d);
    return {
      dimension: d,
      nivelReal: dim?.nivel ?? null,
      nivelCuestionario: dim?.nivelCuestionario ?? null,
    };
  });
  const seleccion = evaluaciones.find((e) => e.id === selectedId) ?? null;
  // La conclusión es editable salvo evaluación congelada (aprobada/obsoleta).
  const mutable = seleccion?.estado !== 'APROBADA' && seleccion?.estado !== 'OBSOLETA';
  const nivelGlobal = heatmap?.evaluacion.nivelGlobal ?? null;
  const cobertura = heatmap?.evaluacion.coberturaModelo ?? null;
  const nivelGlobalCuest = heatmap?.evaluacion.nivelGlobalCuestionario ?? null;
  const coberturaCuest = heatmap?.evaluacion.coberturaCuestionario ?? null;

  return (
    <div
      style={{
        maxWidth: 1400,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        height: embedded ? '100%' : 'calc(100vh - 124px)',
        padding: '10px 14px',
        overflow: 'auto',
        background: 'var(--wl-background, #ECEEF2)',
        fontFamily: "'DM Sans',sans-serif",
      }}
    >
      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.a1 }}>
          {s('title', 'Madurez de estructura · VALOR·IA 5D')}
        </div>
        <div style={{ flex: 1 }} />
        {evaluaciones.length > 0 && (
          <select
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(e.target.value)}
            style={{
              fontSize: 12,
              padding: '5px 8px',
              borderRadius: 6,
              border: `1px solid ${C.gm}`,
              fontFamily: "'DM Sans',sans-serif",
              color: C.a1,
            }}
          >
            {evaluaciones.map((e) => (
              <option key={e.id} value={e.id}>
                {e.codigo ? `${e.codigo} · ` : ''}
                {new Date(e.fechaEvaluacion).toLocaleDateString()} · {e.tipoEjercicio} · {e.estado}
              </option>
            ))}
          </select>
        )}
        <button
          onClick={crear}
          disabled={busy}
          style={{
            fontSize: 12,
            fontWeight: 700,
            padding: '6px 12px',
            borderRadius: 6,
            border: 'none',
            background: C.a1,
            color: C.w,
            cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy ? 0.5 : 1,
            fontFamily: "'DM Sans',sans-serif",
          }}
        >
          {s('crear', 'Nueva evaluación')}
        </button>
        {seleccion && seleccion.estado === 'BORRADOR' && (
          <button
            onClick={aprobar}
            disabled={busy}
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: '6px 12px',
              borderRadius: 6,
              border: `1px solid ${C.a1}`,
              background: C.w,
              color: C.a1,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.5 : 1,
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            {s('aprobar', 'Aprobar')}
          </button>
        )}
        {seleccion && (
          <button
            onClick={clonar}
            disabled={busy}
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: '6px 12px',
              borderRadius: 6,
              border: `1px solid ${C.a2}`,
              background: C.w,
              color: C.a2,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.5 : 1,
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            {s('generarDesde', 'Generar a partir de este')}
          </button>
        )}
      </div>

      {error && (
        <div
          style={{
            background: '#FDECEC',
            border: `1px solid ${ROJO}`,
            color: '#7B0000',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      {/* ── Empty state ── */}
      {evaluaciones.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: C.go,
            gap: 8,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, color: C.a1 }}>
            {s('sinEvaluaciones', 'Sin evaluaciones de madurez')}
          </div>
          <div style={{ fontSize: 13 }}>
            {s(
              'sinEvaluacionesDesc',
              'Crea una evaluación para medir la madurez de la estructura.',
            )}
          </div>
        </div>
      ) : (
        <>
          {/* ── KPI row ── */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 10,
              flexShrink: 0,
            }}
          >
            {/* Nivel global REAL (organigrama) */}
            <div
              style={{ ...cardStyle, borderTopColor: nivelGlobal ? hexNivel(nivelGlobal) : C.a1 }}
            >
              <div
                style={{ fontSize: 9, fontWeight: 700, color: C.go, textTransform: 'uppercase' }}
              >
                {s('nivelGlobalReal', 'Nivel global · Real')}
              </div>
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 800,
                  color: nivelGlobal ? hexNivel(nivelGlobal) : C.go,
                  lineHeight: 1.2,
                }}
              >
                {nivelGlobal ? `${nivLabel(nivelGlobal)}` : '—'}
              </div>
            </div>
            {/* Nivel global CUESTIONARIO (percepción) */}
            <div
              style={{
                ...cardStyle,
                borderTopColor: nivelGlobalCuest ? hexNivel(nivelGlobalCuest) : C.a2,
              }}
            >
              <div
                style={{ fontSize: 9, fontWeight: 700, color: C.go, textTransform: 'uppercase' }}
              >
                {s('nivelGlobalCuest', 'Nivel global · Cuestionario')}
              </div>
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 800,
                  color: nivelGlobalCuest ? hexNivel(nivelGlobalCuest) : C.go,
                  lineHeight: 1.2,
                }}
              >
                {nivelGlobalCuest ? `${nivLabel(nivelGlobalCuest)}` : '—'}
              </div>
            </div>
            <div style={cardStyle}>
              <div
                style={{ fontSize: 9, fontWeight: 700, color: C.go, textTransform: 'uppercase' }}
              >
                {s('cobertura', 'Cobertura del modelo')}
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: C.a1, lineHeight: 1.2 }}>
                {cobertura != null ? `${Math.round(Number(cobertura))}%` : '—'}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.go, marginTop: 2 }}>
                {s('coberturaCuest', 'Cuestionario')}:{' '}
                {coberturaCuest != null ? `${Math.round(Number(coberturaCuest))}%` : '—'}
              </div>
            </div>
            <div style={cardStyle}>
              <div
                style={{ fontSize: 9, fontWeight: 700, color: C.go, textTransform: 'uppercase' }}
              >
                {s('estado', 'Estado')}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.a2, lineHeight: 1.4 }}>
                {seleccion?.estado ?? '—'}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.go, marginTop: 2 }}>
                {s('codigo', 'Código')}: {seleccion?.codigo ?? '—'}
              </div>
            </div>
          </div>

          {/* ── Toggle de lectura: Real (organigrama) / Cuestionario (percepción) ── */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            <span
              style={{ fontSize: 10, fontWeight: 700, color: C.go, textTransform: 'uppercase' }}
            >
              {s('lectura', 'Lectura')}:
            </span>
            {(['real', 'cuestionario'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setVista(v)}
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '4px 12px',
                  borderRadius: 6,
                  border: `1px solid ${C.a1}`,
                  background: vista === v ? C.a1 : C.w,
                  color: vista === v ? C.w : C.a1,
                  cursor: 'pointer',
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                {v === 'real'
                  ? s('vistaReal', 'Real (organigrama)')
                  : s('vistaCuestionario', 'Cuestionario')}
              </button>
            ))}
          </div>

          {/* ── Fila principal: izquierda (heatmap+radar+evolución) · derecha (cuestionario acotado a la altura de la izquierda) ── */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.5fr 1fr',
              gap: 10,
              alignItems: 'stretch',
              flexShrink: 0,
            }}
          >
            {/* COLUMNA IZQUIERDA: heatmap + radar + evolución apilados */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
              {/* Heatmap 5×5 */}
              <div style={cardStyle}>
                <SecTitle>{s('heatmap', 'Heatmap 5×5 (dimensión × nivel)')}</SecTitle>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '4px 6px', color: C.go }} />
                      {NIVELES.map((n) => (
                        <th
                          key={n}
                          style={{
                            padding: '4px 6px',
                            color: C.a1,
                            fontWeight: 700,
                            fontSize: 10,
                            textAlign: 'center',
                          }}
                        >
                          {n.replace('_', '-')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {DIM_ORDER.map((d) => {
                      const dim = dimMap.get(d);
                      const nivelActivo = nivelDe(dim);
                      const alcanzado = nivelActivo ? NIVEL_NUM[nivelActivo] : 0;
                      return (
                        <tr key={d}>
                          <td
                            title={`${PREGUNTA_DIMENSION[d]}\n\nMarco teórico: ${MARCO_TEORICO[d]}`}
                            style={{
                              padding: '4px 6px',
                              fontWeight: 600,
                              color: C.a1,
                              whiteSpace: 'nowrap',
                              cursor: 'help',
                            }}
                          >
                            {dimLabel(d)}
                          </td>
                          {NIVELES.map((n) => {
                            const val = NIVEL_NUM[n];
                            const esAlcanzado = !!nivelActivo && val === alcanzado;
                            let bg: string = C.gc;
                            if (nivelActivo) {
                              if (val === alcanzado) bg = hexNivel(nivelActivo);
                              else if (val < alcanzado) bg = `${hexNivel(nivelActivo)}33`;
                            }
                            return (
                              <td key={n} style={{ padding: 2 }}>
                                <div
                                  title={`${dimLabel(d)} — ${n.replace('_', '-')} ${NIVEL_INFO[n].nombre}\n${descriptorCelda(d, n)}`}
                                  style={{
                                    height: 28,
                                    borderRadius: 4,
                                    background: bg,
                                    border: `0.5px solid ${C.gm}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: C.w,
                                    fontWeight: 800,
                                    fontSize: 10,
                                    cursor: 'help',
                                  }}
                                >
                                  {esAlcanzado ? n.replace('_', '-') : ''}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* Leyenda de niveles — define qué significa cada valor del modelo 5D */}
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: C.go,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginBottom: 2,
                    }}
                  >
                    {s('niveles', 'Niveles — pasa el ratón por una celda para el detalle')}
                  </div>
                  {NIVELES_5D.map((nv) => (
                    <div
                      key={nv.nivel}
                      title={nv.resumen}
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 6,
                        fontSize: 10,
                        cursor: 'help',
                      }}
                    >
                      <span
                        style={{
                          width: 26,
                          flexShrink: 0,
                          fontWeight: 800,
                          color:
                            nv.color === 'VERDE'
                              ? '#16A34A'
                              : nv.color === 'AMARILLO'
                                ? '#E0A800'
                                : '#DC2626',
                        }}
                      >
                        {nv.etiqueta}
                      </span>
                      <span style={{ flexShrink: 0, fontWeight: 700, color: C.a1 }}>
                        {nv.nombre}
                      </span>
                      <span
                        style={{
                          color: C.go,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {nv.resumen}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Radar */}
              <div style={cardStyle}>
                <SecTitle>{s('radar', 'Radar 5 dimensiones')}</SecTitle>
                <div style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData} outerRadius="72%">
                      <PolarGrid stroke={C.gm} />
                      <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 9, fill: C.go }} />
                      <PolarRadiusAxis
                        domain={[0, 5]}
                        tickCount={6}
                        tick={{ fontSize: 8, fill: C.go }}
                      />
                      <Radar
                        dataKey="nivel"
                        stroke={C.a1}
                        fill={C.a2}
                        fillOpacity={0.45}
                        isAnimationActive={false}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Evolución del nivel global */}
              <div style={{ ...cardStyle, height: 240 }}>
                <SecTitle>{s('timeline', 'Evolución del nivel global')}</SecTitle>
                <MadurezTimeline
                  evaluaciones={evaluaciones}
                  emptyLabel={s('timelineEmpty', 'Aún no hay histórico suficiente.')}
                />
              </div>

              {/* Conclusión del ingeniero de gobernanza (captura cualitativa colapsable) */}
              <div style={cardStyle}>
                <SecTitle>
                  {s('conclusionConsultor', 'Conclusión del ingeniero de gobernanza')}
                </SecTitle>
                <button
                  type="button"
                  onClick={() => setMostrarConclusion((v) => !v)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.a2,
                    textTransform: 'uppercase',
                    marginBottom: 4,
                    fontFamily: "'DM Sans',sans-serif",
                  }}
                >
                  <span style={{ fontSize: 10 }}>{mostrarConclusion ? '▾' : '▸'}</span>
                  {s('conclusionConsultor', 'Conclusión del ingeniero de gobernanza')}{' '}
                  <span style={{ color: C.ce, fontWeight: 700 }}>
                    {s('opcional', '(opcional)')}
                  </span>
                </button>
                {mostrarConclusion &&
                  (mutable ? (
                    <>
                      <textarea
                        value={observaciones}
                        onChange={(e) => {
                          setObservaciones(e.target.value);
                          setObsGuardado(false);
                        }}
                        rows={3}
                        placeholder={s(
                          'conclusionPlaceholder',
                          'Tu lectura experta del contexto, más allá del dato objetivo…',
                        )}
                        style={{
                          width: '100%',
                          fontSize: 12,
                          fontFamily: "'DM Sans',sans-serif",
                          border: `1px solid ${C.gm}`,
                          borderRadius: 6,
                          padding: 8,
                          resize: 'vertical',
                          background: C.w,
                          boxSizing: 'border-box',
                        }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                        <button
                          onClick={guardarObs}
                          disabled={savingObs}
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '6px 12px',
                            borderRadius: 6,
                            border: 'none',
                            background: C.a2,
                            color: C.w,
                            cursor: savingObs ? 'not-allowed' : 'pointer',
                            opacity: savingObs ? 0.5 : 1,
                            fontFamily: "'DM Sans',sans-serif",
                          }}
                        >
                          {s('guardarConclusion', 'Guardar conclusión')}
                        </button>
                        {obsGuardado && (
                          <span style={{ color: VERDE, fontSize: 12 }}>
                            {s('guardado', 'Guardado')}
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <textarea
                        value={observaciones}
                        readOnly
                        rows={3}
                        style={{
                          width: '100%',
                          fontSize: 12,
                          fontFamily: "'DM Sans',sans-serif",
                          border: `1px solid ${C.gm}`,
                          borderRadius: 6,
                          padding: 8,
                          resize: 'vertical',
                          background: '#F7F7F7',
                          boxSizing: 'border-box',
                        }}
                      />
                      <div style={{ fontSize: 11, color: C.go, marginTop: 4, fontStyle: 'italic' }}>
                        🔒 {s('conclusionBloqueada', 'Diagnóstico aprobado · solo lectura')}
                      </div>
                    </>
                  ))}
              </div>
            </div>

            {/* COLUMNA DERECHA: cuestionario VALOR·IA, acotado a la altura de la columna izquierda.
                El card va en position:absolute/inset:0 dentro de una celda relative+minHeight:0,
                así su contenido queda FUERA de flujo y NO contribuye a la altura de la fila;
                la fila la fija la columna izquierda (stretch) y el formulario hace scroll dentro. */}
            <div style={{ position: 'relative', minHeight: 0 }}>
              <div
                style={{
                  ...cardStyle,
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <SecTitle>{s('cuestionario', 'Cuestionario VALOR·IA')}</SecTitle>
                {selectedId ? (
                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    <CuestionarioForm
                      evaluacionId={selectedId}
                      dimLabel={dimLabel}
                      readOnly={seleccion?.estado === 'APROBADA'}
                      labels={{
                        intro: s(
                          'cuestionarioIntro',
                          'Responde según tu percepción (0 = nunca/nada, 4 = siempre/total).',
                        ),
                        guardar: s('guardar', 'Guardar respuestas'),
                        guardando: s('guardando', 'Guardando…'),
                        guardado: s('guardado', 'Respuestas guardadas'),
                        sinPreguntas: s('cargandoCuestionario', 'Cargando cuestionario…'),
                        nota: s('cuestionarioNota', 'Añadir nota cualitativa'),
                        lecturaSolo: s(
                          'cuestionarioLecturaSolo',
                          'Diagnóstico aprobado · cuestionario congelado (solo lectura)',
                        ),
                      }}
                      onSaved={() => {
                        const evalId = selectedId;
                        void sembrarMadurezCuestionario(evalId)
                          .then(() => fetchMadurezHeatmap(evalId))
                          .then((r) => {
                            // Descartar si el usuario ya cambió de evaluación (heatmap stale).
                            if (selectedIdRef.current === evalId) setHeatmap(r.data);
                          })
                          .catch((err: Error) => setError(err.message));
                      }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: C.go,
                      fontSize: 11,
                      fontStyle: 'italic',
                      textAlign: 'center',
                      padding: 8,
                    }}
                  >
                    {s(
                      'cuestionarioHint',
                      'Selecciona una evaluación para responder y triangular el dato objetivo con la percepción.',
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Análisis V·IA Governance (3 cards apilados, siempre visible) ── */}
          {selectedId && seleccion && (
            <div style={{ flexShrink: 0 }}>
              <MadurezAnalisisIA
                key={selectedId}
                evaluacionId={selectedId}
                observacionesIniciales={seleccion.observaciones}
                dimLabel={dimLabel}
                dimensiones={dimensionesAnalisis}
                onChanged={() => {
                  void cargarLista(selectedId).catch(() => undefined);
                }}
              />
            </div>
          )}
          <div style={{ flexShrink: 0 }}>
            <EscenariosWhatIf />
          </div>
        </>
      )}
    </div>
  );
}
